import { INTERCOM_APP_ID, INTERCOM_REGION } from './config';

/**
 * Attributes the messenger is booted with. `user_id` / `user_hash` come from
 * `GET /intercom/identity` (Identity Verification); the rest are display traits
 * mapped from the auth profile. Empty object = anonymous visitor.
 */
export interface IntercomBootSettings {
  user_id?: string;
  user_hash?: string;
  name?: string;
  email?: string;
  /** Account creation time, Unix seconds — Intercom's expected format. */
  created_at?: number;
}

/**
 * Minimal seam over the Intercom SDK so the boot/identity reconciliation logic
 * can be unit-tested against a fake. `boot` (re)initialises the messenger with
 * the given settings; `shutdown` clears the current session (used before
 * re-identifying on a user switch / logout).
 */
export interface IntercomController {
  boot(settings: IntercomBootSettings): void;
  shutdown(): void;
}

/** The SDK functions we drive, after normalising the module's interop shape. */
interface IntercomApi {
  boot: (settings: Record<string, unknown>) => void;
  shutdown: () => void;
  show: () => void;
}

/**
 * `@intercom/messenger-js-sdk` is published as CommonJS, so the callable and its
 * helpers land in different places depending on the bundler's interop: esbuild's
 * dep-optimizer nests everything under `.default` (`mod.default.Intercom`,
 * `mod.default.shutdown`), while a clean ESM interop hoists them to named/default
 * exports. The `.d.ts` only describes the latter, so calling `mod.default(...)`
 * blindly throws "default is not a function" at runtime. Resolve the real
 * callables defensively against both shapes.
 */
function resolveApi(mod: unknown): IntercomApi {
  const ns = (mod ?? {}) as Record<string, unknown>;
  const def = ns.default;
  const exportsObj = (def && typeof def === 'object' ? def : ns) as Record<string, unknown>;
  const boot = (exportsObj.Intercom ?? exportsObj.default ?? def) as IntercomApi['boot'];
  const shutdown = (exportsObj.shutdown ?? ns.shutdown) as IntercomApi['shutdown'];
  const show = (exportsObj.show ?? ns.show) as IntercomApi['show'];
  return { boot, shutdown, show };
}

// Lazy, single-flight import: the SDK is only fetched once an enabled messenger
// actually boots, so a disabled build (no app id) never pulls it into a chunk —
// mirrors how the analytics replay plugin dynamically imports its recorder.
let apiPromise: Promise<IntercomApi> | null = null;
function loadApi(): Promise<IntercomApi> {
  apiPromise ??= import('@intercom/messenger-js-sdk').then(resolveApi);
  return apiPromise;
}

function baseSettings(): { app_id: string; hide_default_launcher: boolean; region?: 'us' | 'eu' | 'ap' } {
  return {
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: true,
    ...(INTERCOM_REGION ? { region: INTERCOM_REGION as 'us' | 'eu' | 'ap' } : {}),
  };
}

/**
 * Real controller backed by the lazily-loaded SDK. `boot` (`Intercom(props)`)
 * initialises (settling the region) and boots in one call; calling it again
 * after `shutdown()` cleanly re-boots. Failures are swallowed — a support widget
 * must never break the app.
 *
 * Ordering: `boot`/`shutdown` are fire-and-forget, but every call chains off the
 * SAME singleton `loadApi()` promise, so their `.then` callbacks run in
 * enqueue order (FIFO microtasks). A sequence of reconcile calls therefore
 * reaches the SDK in the order issued, and `reconcileIntercom` is idempotent
 * per-state, so the messenger converges to the latest auth state.
 */
export const intercomController: IntercomController = {
  boot: (settings) => {
    void loadApi()
      .then((api) => api.boot({ ...baseSettings(), ...settings }))
      .catch(() => undefined);
  },
  shutdown: () => {
    void loadApi()
      .then((api) => api.shutdown())
      .catch(() => undefined);
  },
};

/**
 * Open the Intercom messenger panel (Settings "Support" entry point).
 * Same fire-and-forget/swallow-failures contract as the controller: a support
 * widget must never break the app. No-op when Intercom is disabled — callers
 * should hide the entry point via {@link isIntercomEnabled} instead.
 */
export function showIntercomMessenger(): void {
  void loadApi()
    .then((api) => api.show?.())
    .catch(() => undefined);
}
